import { InertiaLinkProps } from '@inertiajs/react';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export function isSameUrl(
    url1: NonNullable<InertiaLinkProps['href']>,
    url2: NonNullable<InertiaLinkProps['href']>,
) {
    return resolveUrl(url1) === resolveUrl(url2);
}

export function resolveUrl(url: NonNullable<InertiaLinkProps['href']>): string {
    return typeof url === 'string' ? url : url.url;
}

export function applyCNPJMask(value: string): string {
    const numbers = value.replace(/\D/g, '');

    const limited = numbers.slice(0, 14);

    if (limited.length <= 2) return limited;
    if (limited.length <= 5)
        return `${limited.slice(0, 2)}.${limited.slice(2)}`;
    if (limited.length <= 8)
        return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5)}`;
    if (limited.length <= 12)
        return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5, 8)}/${limited.slice(8)}`;
    return `${limited.slice(0, 2)}.${limited.slice(2, 5)}.${limited.slice(5, 8)}/${limited.slice(8, 12)}-${limited.slice(12)}`;
}

export function applyCPFMask(value: string): string {
    const numbers = value.replace(/\D/g, '').slice(0, 11);

    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6)
        return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    if (numbers.length <= 9)
        return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9)}`;
}

export function applyCEPMask(value: string): string {
    const numbers = value.replace(/\D/g, '').slice(0, 8);

    if (numbers.length <= 5) return numbers;
    return `${numbers.slice(0, 5)}-${numbers.slice(5)}`;
}

export function applyPhoneMask(value: string): string {
    const numbers = value.replace(/\D/g, '').slice(0, 11);

    if (numbers.length <= 2) return numbers.length ? `(${numbers}` : '';
    if (numbers.length <= 6)
        return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    if (numbers.length <= 10)
        return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
}

// Brazilian States Abbreviation Mapping (UF: State Name)
export const BrazilianStates = {
    AC: 'Acre',
    AL: 'Alagoas',
    AP: 'Amapá',
    AM: 'Amazonas',
    BA: 'Bahia',
    CE: 'Ceará',
    DF: 'Distrito Federal',
    ES: 'Espírito Santo',
    GO: 'Goiás',
    MA: 'Maranhão',
    MT: 'Mato Grosso',
    MS: 'Mato Grosso do Sul',
    MG: 'Minas Gerais',
    PA: 'Pará',
    PB: 'Paraíba',
    PR: 'Paraná',
    PE: 'Pernambuco',
    PI: 'Piauí',
    RJ: 'Rio de Janeiro',
    RN: 'Rio Grande do Norte',
    RS: 'Rio Grande do Sul',
    RO: 'Rondônia',
    RR: 'Roraima',
    SC: 'Santa Catarina',
    SP: 'São Paulo',
    SE: 'Sergipe',
    TO: 'Tocantins',
};
export const UFOptions = Object.keys(BrazilianStates);

export const OperationNatureOptions = [
    { value: '1', label: 'Tributação no município' },
    { value: '2', label: 'Tributação fora do município' },
    { value: '3', label: 'Isenção' },
    { value: '4', label: 'Imune' },
    { value: '5', label: 'Exigibilidade suspensa por decisão judicial' },
    {
        value: '6',
        label: 'Exigibilidade suspensa por procedimento administrativo',
    },
];

export function isValidMoney(value: string) {
    const v = value.trim();
    if (!v) return true;
    return /^\d+([.,]\d{1,2})?$/.test(v);
}

const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/;

// Slices instead of parsing: new Date() on the long form loses a day in UTC-3.
export function toCalendarDay(value: string | null | undefined): string | null {
    if (!value) return null;
    const day = value.slice(0, 10);
    return CALENDAR_DAY.test(day) ? day : null;
}

// Same day as pt-BR text, built at local noon so it never crosses a timezone.
export function formatCalendarDate(value: string | null | undefined): string {
    const day = toCalendarDay(value);
    if (!day) return '-';
    return new Date(`${day}T12:00:00`).toLocaleDateString('pt-BR');
}

export function formatCurrency(
    value: string | number | null | undefined,
): string {
    if (value === null || value === undefined) return '-';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '-';
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(num);
}

export function formatCurrencyInput(value: string): string {
    const numbers = value.replace(/\D/g, '');
    if (!numbers) return '';
    const amount = parseFloat(numbers) / 100;
    return amount.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

export function parseCurrencyInput(value: string): string {
    const numbers = value.replace(/\D/g, '');
    if (!numbers) return '';
    return (parseFloat(numbers) / 100).toFixed(2);
}

export function numberToCurrencyInput(
    value: number | string | null | undefined,
): string {
    if (value === null || value === undefined || value === '') return '';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '';
    return num.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}
